import React, { useEffect, useRef, useState } from 'react';
import { Text } from 'react-native';

// Decelerating ease: fast at the start, eases into the final value so the
// number "lands" rather than stopping abruptly.
const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

/**
 * CountUp - animates a number rolling up to `value` (odometer style).
 *
 * Purely web app, so requestAnimationFrame is always available. The first time
 * a real value arrives it rolls from 0; later changes roll from the previously
 * shown value so refetches nudge rather than re-spin from zero.
 *
 * @param {Object} props
 * @param {number} props.value        Final value to land on.
 * @param {number} [props.duration]   Animation length in ms (default 1000).
 * @param {boolean} [props.animate]   When false, render `value` immediately.
 * @param {string} [props.locale]     Intl locale for thousands separators.
 * @param {Function} [props.format]   Maps the current number to a display
 *                                    string (e.g. `n => `£${n/1000}k``). When
 *                                    omitted, falls back to `toLocaleString`.
 * @param {Object} [props.style]      Text style.
 */
const CountUp = ({
  value = 0,
  duration = 1000,
  animate = true,
  locale = 'en-GB',
  format,
  style,
  ...rest
}) => {
  const [display, setDisplay] = useState(animate ? 0 : value);
  // Latest rendered value, read at the start of each new animation so we tween
  // from where we are now instead of snapping back to 0.
  const displayRef = useRef(animate ? 0 : value);
  const rafRef = useRef(null);

  const setBoth = (v) => {
    displayRef.current = v;
    setDisplay(v);
  };

  useEffect(() => {
    if (!animate || value === displayRef.current) {
      setBoth(value);
      return undefined;
    }

    const from = displayRef.current;
    const target = value;
    let startTs = null;

    const tick = (now) => {
      if (startTs === null) startTs = now;
      const t = Math.min(1, (now - startTs) / duration);
      const next = Math.round(from + (target - from) * easeOutExpo(t));
      setBoth(next);
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setBoth(target);
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
    // displayRef is a ref; intentionally only re-run when the target changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, duration, animate]);

  return (
    <Text style={style} {...rest}>
      {format ? format(display) : Number(display).toLocaleString(locale)}
    </Text>
  );
};

export default CountUp;
